/**
 * Wraps a bw-ui FormField + Input in the state management for a field on a
 * particular step.
 */
import React, { useState } from 'react';
import { FormField } from '@common/bw-ui/ui/form-field';
import { Input } from '@common/bw-ui/ui/input';
import { Spinner } from '@common/bw-ui/ui/spinner';
import { AlertCircleSolidAnimatedIcon, SuccessCircleAnimatedIcon } from '@common/bw-ui/icons';
import { cn } from '@common/bw-ui/lib/utils';
import ISignupUser from '@/app/(auth)/signup/types/ISignupUser';
import { useSignupStep } from '@/app/(auth)/signup/components/signupStep/SignupStepContext';

export interface StepInputProps {
  field: keyof ISignupUser;
  label: React.ReactNode;
  id: string;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  required?: boolean;
  /** Hint text rendered between the label and the input */
  description?: React.ReactNode;
  readonly?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  /**
   * Overrides the computed valid state (green field + check icon), for fields
   * whose validity goes beyond the step's schema, e.g. the email uniqueness check
   */
  success?: boolean;
  overrideErrorMessage?: string;
  className?: string;
  autoComplete?: string;
}

export function StepInput(props: StepInputProps) {
  // We wait until the input has been blurred before we show an error message - this prevents the error message
  // showing on every keystroke, which breaks screen readers
  const [hasBlurred, setHasBlurred] = useState(false);

  const { details, setDetails, fieldErrors } = useSignupStep();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!props.readonly) {
      const value = e.target.value;
      const finalValue = props.type === 'email' ? value.trim() : value;
      setDetails({ ...details, [props.field]: finalValue });
    }
  };

  const value = details[props.field] || '';

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBlurred(true);
    setDetails({ ...details, [props.field]: e.target.value.trim() });
  };

  const fieldError = hasBlurred ? fieldErrors[props.field]?.join(' ') : undefined;
  const error = props.overrideErrorMessage ?? (fieldError || undefined);
  const hasError = Boolean(error) || props.hasError;

  // Success is gated on live validation (not the blur-gated error display) so a
  // half-typed invalid value never shows green. Readonly fields skip the computed
  // default, but an explicit success (e.g. the locked email) still shows green -
  // it reassures the user the value is settled
  const isValid = Boolean(value) && !fieldErrors[props.field]?.length && !props.readonly;
  const showSuccess = (props.success ?? isValid) && !hasError && !props.isLoading;

  return (
    <FormField
      id={props.id}
      label={props.label}
      requirement={props.required ? 'required' : 'optional'}
      description={props.description}
      error={error}
      className={props.className}>
      <div className="tw:relative tw:w-full">
        <Input
          variant="form"
          name={props.field}
          type={props.type}
          placeholder={props.placeholder}
          value={String(value)}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={props.readonly}
          autoComplete={props.autoComplete}
          aria-invalid={hasError || undefined}
          data-success={showSuccess ? '' : undefined}
          className={cn(
            props.readonly && !showSuccess && 'tw:bg-surface-muted',
            (props.isLoading || showSuccess || hasError) && 'tw:pr-9',
          )}
        />
        {props.isLoading && (
          <Spinner className="tw:absolute tw:right-2.5 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted" />
        )}
        {showSuccess && (
          <SuccessCircleAnimatedIcon
            data-slot="success-icon"
            className="tw:pointer-events-none tw:absolute tw:right-2.5 tw:top-1/2 tw:size-5 tw:-translate-y-1/2 tw:text-success"
            aria-hidden="true"
          />
        )}
        {hasError && !props.isLoading && (
          <AlertCircleSolidAnimatedIcon
            data-slot="error-icon"
            className="tw:pointer-events-none tw:absolute tw:right-2.5 tw:top-1/2 tw:size-5 tw:-translate-y-1/2 tw:text-destructive"
            aria-hidden="true"
          />
        )}
      </div>
    </FormField>
  );
}
